<template>
  <div>
    <div class="form-section">
      <h4 class="member-details-form-section-header">Student declaration</h4>
      <caption class="required-field-indicator">* Indicates required field</caption>
      <FormField
        labelText="Are you a student?"
        :helperText="studentDeclarationStatus !== null ? refData.studentDeclarationHelpText.informationText : ''"
        :additionalLabel="refData.studentDeclarationInformationText.informationText"
      >
        <ul role="radiogroup" class="radio-group large">
          <li v-for="option in studentDeclarationOptions" :key="`declaration-${option.returnValue}`">
            <input
              type="radio"
              v-model="studentDeclarationStatus"
              :id="`declaration-${option.returnValue}`"
              :value="option.returnValue"
              name="declarationOption"
              :disabled="fieldDisabled"
              @click="switchDeclaration(option.returnValue)"
            />
            <label :for="`declaration-${option.returnValue}`">{{ option.display }}</label>
          </li>
        </ul>
        <div v-if="!studentDeclarationStatus && isStudent !== null">
          <Alert
            v-if="this.currentAccessLevel === AccessLevel.POLICY_HOLDER_ACCESS || this.currentAccessLevel === AccessLevel.FULL_ACCESS"
            :message="`Your dependant has not been listed as a student dependant, you may be required to pay extended family premiums on your policy.`"
          />
        </div>
        <div :v-if="validationMessaging.message && validationMessaging.severity">
          <Alert :message="validationMessaging.message" :alertType="validationMessaging.severity" :isPlainText="false" />
        </div>
      </FormField>
    </div>

    <div v-if="studentDeclarationStatus" class="form-section">
      <FormField
        labelText="Name of the school, college, university or TAFE at which you are studying"
        id="institute-name"
        autocomplete="institute"
        v-model.lazy="v$.memberDetails.studentDeclaration.instituteName.$model"
        :error="v$.memberDetails.studentDeclaration.instituteName.$error"
        :error-message="getNameErrorMessage"
        :disabled="fieldDisabled"
        type="text"
      ></FormField>
    </div>

    <div v-if="studentDeclarationStatus" class="form-section">
      <FormField id="year-of-study" labelText="Year of study" :required="false">
        <p>{{ yearOfStudy }}</p>
      </FormField>
    </div>

    <div v-if="studentDeclarationStatus" class="form-section">
      <FormField id="declare-student-info">
        <div class="field checkbox">
          <input
            type="checkbox"
            id="declareInfo"
            v-model="v$.isInfoDeclared.$model"
            @change="v$.isInfoDeclared.$touch()"
            @keyup.enter.space="v$.isInfoDeclared.$model = v$.isInfoDeclare.$invalidd.$model"
            :disabled="fieldDisabled"
          />
          <label for="declareInfo">
            I declare the details that I have provided are true and correct.
            <span class="required-input">*</span>
          </label>
          <label v-if="v$.isInfoDeclared.$error" for="declareInfo" class="error">Please include a response for Declaration.</label>
        </div>
      </FormField>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import { FormField, Alert } from '@/components';
import MemberDetailsMixin from '@/views/MemberDetails/MemberDetailsFormSteps/MemberDetailsMixin';
import { UpdateMemberDetailsStepNames, AccessLevel, OperationType } from '@/enums';
import { getStudentDeclarationYear, isYearLeapYear, stringWithInvalidSpace, under21Years11Month, checkDateWithin30Days, calculateAge } from '@general/js/utils';
import { JOIN_NOW_NAME_VALIDATOR } from '@general/js/validators';
import { NameErrorMessage } from '@general/js/enums/ErrorMessageEnum';
import { CoverComposition } from '@general/js/enums';

export default {
  setup() { return { v$: useVuelidate() }; },

  name: UpdateMemberDetailsStepNames.step5,
  mixins: [MemberDetailsMixin],

  data() {
    return {
      studentDeclarationOptions: [
        { display: 'Yes', returnValue: true },
        { display: 'No', returnValue: false },
      ],
      isInfoDeclared: null,
      isStudent: null,
      fieldDisabled: true,
      instituteName: '',
      yearOfStudy: null,
      savedInstituteName: '',
    };
  },

  validations() {
    return this.validationObj;
  },

  watch: {
    instituteName: {
      handler(newValue) {
        const newMemberDetails = { ...this.memberDetails };
        newMemberDetails.studentDeclaration = { ...newMemberDetails.studentDeclaration, instituteName: newValue };
        this.updateMemberDetails(newMemberDetails);
      },
    },
  },

  components: {
    FormField,
    Alert,
  },

  computed: {
    validationObj() {
      let validations = {};

      // Validate composition, access level and cover type using custom property
      validations = {
        invalidCover: {
          valid() {
            return this.invalidCover === false;
          },
        },
      };

      if (this.isStudent === null || this.isStudent) {
        validations = {
          ...validations,
          memberDetails: {
            studentDeclaration: {
              instituteName: {
                JOIN_NOW_NAME_VALIDATOR,
                stringWithInvalidSpace,
                maxLength: maxLength(100),
                required,
              },
            },
          },
          isInfoDeclared: {
            checked() {
              return this.isInfoDeclared === true;
            },
            required,
          },
        };
        return validations;
      }

      validations = { ...validations, memberDetails: {} };
      return validations;
    },

    getNameErrorMessage() {
      const instituteName = this.v$.memberDetails.studentDeclaration?.instituteName;
      if (instituteName.$error && !instituteName.maxLength) {
        return NameErrorMessage.IS_OVER_LENGTH;
      }
      if (instituteName.$error && !instituteName.JOIN_NOW_NAME_VALIDATOR) {
        return NameErrorMessage.IS_INVALID;
      }

      if (instituteName.$error && !instituteName.required) {
        return `${NameErrorMessage.IS_EMPTY} Institute name.`;
      }

      return '';
    },

    validationMessaging() {
      let returnValue = { message: null, severity: null };

      const isPolicyHolderAccess = this.currentAccessLevel === AccessLevel.POLICY_HOLDER_ACCESS;
      const isFullOrSelfAccess = this.currentAccessLevel === AccessLevel.FULL_ACCESS || this.currentAccessLevel === AccessLevel.SELF_ACCESS;
      const isFamilyOrSingleParentFamily = this.composition === CoverComposition.FAMILY || this.composition === CoverComposition.SINGLE_PARENT;
      const isFamilyPlusOrSingleParentFamilyPlus = this.composition === CoverComposition.FAMILY_PLUS || this.composition === CoverComposition.SINGLE_PARENT_PLUS;
      const isHospitalOrExtrasOnly = this.hospitalOnly || this.extrasOnly;

      // Declared as Non-Student
      if (!this.isStudent && this.isStudent != null) {
        // Has Hospital and Extras Cover and is Family or Single Parent
        if (this.hospitalExtrasCover && isFamilyOrSingleParentFamily) {
          if (isFullOrSelfAccess) {
            returnValue = { message: 'You are unable to make this change, please ask your policy holder to make this change.', severity: 'error' };
          }
        }
        // Has Hospital only or Extras only cover and only 1 student dependent on the policy
        if (isHospitalOrExtrasOnly && this.studentDependents === 1) {
          if (isPolicyHolderAccess) {
            returnValue = {
              message:
                'You are unable to make this change on your current product. If you would like to change your product please navigate to <a href="/$memberNumber$/my-cover/change-my-cover/change-my-product/">change my product</a>.',
              severity: 'error',
            };
          }
          if (isFullOrSelfAccess) {
            returnValue = { message: 'You are unable to make this change, please ask your policy holder to make this change.', severity: 'error' };
          }
        }

        // Has Hospital only or Extras only cover and more than 1 student dependent on the policy
        if (isHospitalOrExtrasOnly && this.studentDependents > 1) {
          if (isPolicyHolderAccess) {
            returnValue = {
              message:
                'You are unable to make this change on your current product. If you would like to remove the existing student dependant from your policy, please remove them from your policy via the <a href="/$memberNumber$/my-cover/change-my-cover/add-remove-person/">Add/Remove a person</a> or <a href="/$memberNumber$/my-cover/change-my-cover/change-my-product/">Change your product</a>',
              severity: 'error',
            };
          }
          if (isFullOrSelfAccess) {
            returnValue = { message: 'You are unable to make this change, please ask your policy holder to make this change. ', severity: 'error' };
          }
        }
      }
      // Declared as Student
      if (this.isStudent && this.isStudent != null) {
        // Family+ or Single Parent+ composition and 1 non-student dependent on the policy
        if (isFamilyPlusOrSingleParentFamilyPlus && this.nonStudentDependent === 1) {
          if (isFullOrSelfAccess) {
            returnValue = { message: 'You are unable to make this change, please ask your policy holder to make this change. ', severity: 'error' };
          }
        }
      }

      return {
        ...returnValue,
        message: returnValue.message ? returnValue.message.tokenSwap() : null,
      };
    },
    invalidCover() {
      return this.validationMessaging.severity === 'error' && this.validationMessaging.message;
    },
    hasDeclaredForCurrentYear() {
      return (
        Number(getStudentDeclarationYear(this.memberDetails?.studentDeclaration?.startDate)) === Number(getStudentDeclarationYear(this.getCurrentDate())) &&
        !this.isEmptyString(this.instituteName)
      );
    },
  },

  beforeMount() {
    const underAge = under21Years11Month(this.memberDetails?.memberInformation?.dateOfBirth);
    const isDependantWithinStudentAge =
      this?.memberDetails?.memberInformation?.dateOfBirth &&
      this.isDependant &&
      this.withinStudentAge(this.reverseFormatFormDate(this.memberDetails.memberInformation.dateOfBirth));
    // if member is underage(21y11m) or is not dependant or within student age range (20y11m-30y) then we set the "studentDeclaration" null
    // otherwise prefill the institute name with current instituteName from server
    if (!(underAge || !isDependantWithinStudentAge)) {
      this.instituteName = this.memberDetails.studentDeclaration?.instituteName;
      this.savedInstituteName = this.memberDetails.studentDeclaration?.instituteName;
    }
  },

  mounted() {
    // update current step's validation
    this.updateMemberDetailsFormValid(this.v$.$invalid);
    // if student declaration year is equal to current year then we disable fields on the page
    // otherwise deselected the YES/NO option
    if (this.hasDeclaredForCurrentYear) {
      this.studentDeclarationStatus = true;
      this.isInfoDeclared = true;
      this.fieldDisabled = true;
      this.yearOfStudy = Number(getStudentDeclarationYear(this.getCurrentDate()));
      const updatedDates = this.formatYearIntoStartAndEndDatesForStudentDeclaration(this.yearOfStudy);

      const newMemberDetails = { ...this.memberDetails };
      newMemberDetails.studentDeclaration = {
        instituteName: this.memberDetails?.studentDeclaration?.instituteName || this.savedInstituteName,
        ...updatedDates,
        isStudent: this.hasDeclaredForCurrentYear,
      };

      this.updateMemberDetails(newMemberDetails);
    } else {
      /**
       * form is enabled if one of the following conditions is met:
       * - current date is in between Feb 1st and March 31st
       * - member is turning 21 in 30 days
       */
      const today = new Date(this.getCurrentDate());
      const thisMonth = Number(this.getCurrentDate().split('-')[1]);
      const dob = this.memberDetails?.memberInformation?.dateOfBirth;
      const dobInArray = dob?.split('-') || [];
      const birthdayThisYear = `${dobInArray[2]} / ${dobInArray[1]} / ${today.getFullYear()}`;

      // check current date is between Feb 1st (first day of Feb) and March 31st  (last day of March)
      const isDeclarationPeriod = thisMonth >= 2 && thisMonth <= 3;

      // check member is turning 21 in 30days
      const isTurning21In30days = calculateAge(this.reverseFormatFormDate(dob)) === 20 && checkDateWithin30Days(birthdayThisYear, today);

      if (isDeclarationPeriod || isTurning21In30days) {
        this.studentDeclarationStatus = null;
        this.fieldDisabled = false;
        this.yearOfStudy = Number(today.getFullYear());

        // if student declaration is happening in January, declaration is for last year
        if (thisMonth === 1) {
          this.yearOfStudy -= 1;
        }
      }
    }
  },

  methods: {
    scaleChangeRequired() {
      let returnValue = false;

      // Assumes that the number of student or non-student dependants includes the accessed user
      if (!this.hospitalExtrasCover) {
        returnValue = false;
      }

      // Student to Non-Student
      if (!this.isStudent && this.isStudent != null) {
        // Family/Single Parent -> Family+/Single Parent+
        if (this.nonStudentDependent === 0) {
          returnValue = true;
          // Family+/Single Parent+ -> Family+/Single Parent+
        }
        if (this.nonStudentDependent > 1) {
          returnValue = false;
        }
      }
      // Non-Student to Student
      if (this.isStudent && this.isStudent != null) {
        // Family+/Single Parent+ -> Family/Single Parent
        if (this.nonStudentDependent === 1) {
          returnValue = true;
          // Family+/Single Parent+ -> Family+/Single Parent+
        }
        if (this.nonStudentDependent > 1) {
          returnValue = false;
        }
      }
      this.$emit('scaleChangeRequired', returnValue);
      return returnValue;
    },

    // Switch between YES/NO option
    switchDeclaration(isStudent) {
      this.isStudent = isStudent;
      const newMemberDetails = { ...this.memberDetails };

      newMemberDetails.studentDeclaration = {
        op: OperationType.NONE, // No operation by default
      };

      if (!isStudent) {
        newMemberDetails.studentDeclaration = {
          op: OperationType.REMOVE,
        };
        this.updateMemberDetailsFormValid(false);
      } else {
        this.yearOfStudy = Number(getStudentDeclarationYear(new Date()));

        newMemberDetails.studentDeclaration = {
          op: this.hasDeclaredForCurrentYear ? OperationType.REPLACE : OperationType.ADD,
          isStudent: this.isStudent,
          instituteName: this.memberDetails?.studentDeclaration?.instituteName || this.savedInstituteName,
          ...this.formatYearIntoStartAndEndDatesForStudentDeclaration(this.yearOfStudy),
        };
        this.updateMemberDetailsFormValid(this.v$.$invalid);
      }

      // Check if premium has been impacted
      this.scaleChangeRequired();

      this.updateMemberDetails(newMemberDetails);
    },

    formatYearIntoStartAndEndDatesForStudentDeclaration(yyyyYear) {
      return {
        startDate: `${yyyyYear}-03-01`,
        endDate: isYearLeapYear(yyyyYear + 1) ? `${yyyyYear + 1}-02-29` : `${yyyyYear + 1}-02-28`,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
#declare-student-info {
  font-size: rem(14);
}

label {
  display: block;
  font-weight: $BOLD;
  margin-bottom: 10px;
  white-space: break-spaces;
}
</style>
